<script setup>
import { slugify } from '~~/shared/utilities/slugify';
import SpeakersModal from '~/components/blocks/two-six/speakers/modal.vue';
import Cta from '~/components/blocks/two-six/cta.vue';

const CURRENT_YEAR = 2026;
const PREVIOUS_YEARS = [2025];

const route = useRoute()
const speaker_id = route.params.id

const { data, pending, error, clear, status } = await useAsyncData(`speaker-${speaker_id}`, async () => {
    const fetches = [CURRENT_YEAR, ...PREVIOUS_YEARS].map(async (year) => {
        try {
            const res = await $fetch('/api/v2/speakers', { params: { id: speaker_id, year } });
            const match = res?.speakers?.[0];
            return match ? { year, speaker: match } : null;
        } catch { return null; }
    });
    const results = (await Promise.all(fetches)).filter(Boolean);
    if (!results.length) return null;

    // Prefer the current-year speaker record for hero/bio data; fall back to
    // the most recent available year otherwise.
    const primary = results.find(r => r.year === CURRENT_YEAR) || results[0];

    // Combine sessions across all years the speaker appears in. Each session
    // already carries its own `year` so we split in the modal.
    const allSessions = results.flatMap(r => r.speaker.sessions || []);

    return { speakers: [{ ...primary.speaker, sessions: allSessions }] };
});

if (error.value || !data.value?.speakers?.length) {
    throw createError({
        statusCode: 404,
        statusMessage: 'Page Not Found',
        fatal: true,
    });
}

const speakerSlug = slugify(data.value.speakers[0].name);

if (speaker_id !== speakerSlug) {
    if (import.meta.server) {
        throw navigateTo(`/speakers/${speakerSlug}`, { redirectCode: 301 });
    } else {
        await navigateTo(`/speakers/${speakerSlug}`, { replace: true });
    }
}

const canonicalUrl = `https://unbound.hubspot.com/speakers/${speakerSlug}`;

useRoute().meta.canonical = canonicalUrl;
useHead({
    link: [{ rel: 'canonical', href: canonicalUrl }],
});

useSeoMeta({
    title: data.value.speakers[0].name,
    ogTitle: data.value.speakers[0].name,
    description: data.value.speakers[0].description,
    ogDescription: data.value.speakers[0].description,
    ogUrl: canonicalUrl,
    ogImage: '/social.jpg',
    twitterCard: 'summary_large_image',
});

// is_full changes the second a session fills, but this page is served from the
// worker cache (60s fresh, 24h stale), so a rarely-visited speaker page renders
// capacity from whenever it was last cached — "Limited Capacity" and Save a Seat
// on a session the agenda already shows as full (Nick, Asana 1217802085538908).
// The speaker tray never had this: it fetches on open. Re-fetch the current
// year's record once mounted, as the tray does, and swap in its sessions. Not
// refresh(): that flips `pending` and the template unmounts the page for it.
onMounted(async () => {
    try {
        const res = await $fetch('/api/v2/speakers', { params: { id: speaker_id, year: CURRENT_YEAR, limit: 1 } });
        const live = res?.speakers?.[0];
        const current = data.value?.speakers?.[0];
        if (!current || live?.id !== current.id || !Array.isArray(live.sessions)) return;
        const byId = new Map(live.sessions.map((s) => [s.id, s]));
        data.value = { speakers: [{ ...current, sessions: current.sessions.map((s) => byId.get(s.id) || s) }] };
    } catch { /* keep the cached render */ }
});

const page = computed(() => {
    if (!data.value?.speakers?.length) return false
    return { id: data.value.speakers[0].id, data: data.value.speakers[0], model: 'speakers' }
})

</script>

<template>
    <ProjectPage :page="page" data-model="speaker" v-if="!pending && page">
        <section class="speaker-detail-block relative overflow-hidden text-[var(--cream)]">
            <img src="/img/2026/speakers/detail-page-top-shard.png"
                alt="" aria-hidden="true"
                class="detail-page-shard hidden lg:display-block absolute pointer-events-none z-0" />

            <div class="relative z-[1] px-[var(--box-padding-lr-m)] md:px-[var(--box-padding-lr)] pt-[90px] pb-[60px] md:pt-[150px] md:pb-[90px]">
                <MessLink href="/speakers"
                    class="group inline-flex items-center gap-[11px] no-underline
                        font-hubspot-sans font-medium text-[12px] leading-[1.5] tracking-[0.84px] uppercase
                        text-[var(--cream)] hover:text-white transition-colors
                        mb-[32px] md:mb-[40px]">
                    <svg width="7" height="7" viewBox="0 0 8.5 8.5" fill="none" aria-hidden="true">
                        <path d="M7.75 4.25H0.75M0.75 4.25L4.05556 0.750002M0.75 4.25L4.05556 7.75"
                            stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                    <span class="group-hover:underline decoration-dotted underline-offset-[3px] [text-decoration-skip-ink:auto] [text-underline-position:from-font]">
                        All Speakers
                    </span>
                </MessLink>

                <SpeakersModal :speaker="page.data" cards-theme="dark" />
            </div>
        </section>

        <Cta
            variant="orange"
            headline="Be There at UNBOUND 2026"
            copy="Boston. September 16&ndash;18. Three days designed to help you move beyond outdated playbooks. Break beyond the limits that defined yesterday. Be unstoppable."
            :button="{ url: '/register', label: 'Register Now' }" />
    </ProjectPage>
</template>

<style>
.speaker-detail-block {
    margin-top: calc(var(--header-height) * -1);
    background: linear-gradient(5.04deg, #6D0A7B 2.861%, var(--purple) 20.488%, var(--purple) 41.261%, #680971 92.887%);
    isolation: isolate;
    transform: translateZ(0);
}

/* Dark purple shard sits at top-right of the hero area. */
.speaker-detail-block .detail-page-shard {
    top: 0;
    right: 40px;
    width: min(1080px, 80vw);
    height: auto;
}

/* Speaker modal wrapper + hero text flip to cream on the purple background */
.speaker-detail-block .speaker-detail,
.speaker-detail-block .speaker-hero .speaker-bio {
    color: var(--cream);
}

/* Speaker name + job title render in pure white per Figma */
.speaker-detail-block .speaker-hero > div {
    color: #ffffff;
}

/* "Speaker Sessions:" heading is pure white per Figma */
.speaker-detail-block .speaker-detail > section > h3 {
    color: #ffffff;
}

/* Social icons cream on purple bg */
.speaker-detail-block .speaker-hero-socials {
    color: var(--cream);
}
.speaker-detail-block .speaker-hero-socials .links li svg circle {
    stroke: var(--cream);
}
.speaker-detail-block .speaker-hero-socials .links li svg path {
    fill: var(--cream);
}
.speaker-detail-block .speaker-hero-socials .links li:hover svg circle {
    fill: var(--cream);
    stroke: var(--cream);
}
.speaker-detail-block .speaker-hero-socials .links li:hover svg path {
    fill: var(--magenta);
}
</style>